import { useState } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ImageUpload({ value, onChange, label, testId }) {
    const [uploading, setUploading] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const inputId = `upload-${testId || label || "file"}`.replace(/\s+/g, "-").toLowerCase();

    return (
        <div className="space-y-2">
            {label && (
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</div>
            )}
            <div className="flex items-center gap-3">
                {value ? (
                    <div className="relative h-20 w-20 rounded-lg overflow-hidden border border-white/10 shrink-0">
                        <img src={fileUrl(value)} alt="" className="h-full w-full object-cover" />
                        <button
                            type="button"
                            onClick={() => onChange(null)}
                            className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center"
                            data-testid={`${testId}-clear`}
                            aria-label="Remove image"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                ) : (
                    <div className="h-20 w-20 rounded-lg border border-dashed border-white/15 bg-white/5 flex items-center justify-center text-zinc-500 shrink-0">
                        <Upload className="h-5 w-5" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <input
                        id={inputId}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid={testId}
                    />
                    <label htmlFor={inputId}>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={uploading}
                            onClick={() => document.getElementById(inputId)?.click()}
                            className="cursor-pointer border-white/10 text-zinc-200 bg-transparent hover:bg-white/5"
                        >
                            <Upload className="h-3.5 w-3.5 me-1.5" />
                            {uploading ? "Uploading…" : value ? "Replace" : "Upload image"}
                        </Button>
                    </label>
                    <div className="mt-1 text-[11px] text-zinc-500">PNG · JPG · WEBP · up to 8MB</div>
                </div>
            </div>
        </div>
    );
}
